import React, { useState } from 'react';
import axios from 'axios';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Description, Code as CodeIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import type { Monaco } from '@monaco-editor/react';
import { palette, fonts, difficultyLabel, difficultyColor } from '../theme';
import { challengesAPI, submissionsAPI } from '../services/api';
import { Challenge } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

type Language = 'javascript' | 'python' | 'java' | 'cpp';

const LANGUAGE_LABELS: Record<Language, string> = { javascript: 'JavaScript', python: 'Python', java: 'Java', cpp: 'C++' };

const ChallengeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [mobileView, setMobileView] = useState<'problem' | 'code'>('problem');
  const [codesByLanguage, setCodesByLanguage] = useState<Record<Language, string>>({
    javascript: '',
    python: '',
    java: '',
    cpp: '',
  });
  const [language, setLanguage] = useState<Language>('javascript');
  const [submissionResult, setSubmissionResult] = useState<any>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const { data: challenge, isLoading, error } = useQuery({
    queryKey: ['challenge', id],
    queryFn: () => challengesAPI.getChallenge(id!),
    enabled: !!id,
  });

  const submitMutation = useMutation({
    mutationFn: ({ challengeId, code, language }: { challengeId: string; code: string; language: string }) =>
      submissionsAPI.submitSolution(challengeId, code, language),
    onSuccess: (data) => {
      setSubmissionResult(data);
      queryClient.invalidateQueries({ queryKey: ['challenge', id] });
    },
    onError: (error: any) => {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
        ? error.message
        : 'Submission failed';
      setSubmissionError(message);
    },
  });

  const getDefaultStarterCode = (lang: Language) => {
    const templates: Record<Language, string> = {
      javascript: `// Write your JavaScript solution here\nfunction solve(nums, target) {\n  // TODO: implement solution\n  return [];\n}`,
      python: `# Write your Python solution here\ndef solve(nums, target):\n    # TODO: implement solution\n    pass`,
      java: `// Write your Java solution here\nimport java.util.*;\npublic class Solution {\n    public static int[] solve(int[] nums, int target) {\n        // TODO: implement solution\n        return new int[0];\n    }\n}`,
      cpp: `// Write your C++ solution here\n#include <vector>\nusing namespace std;\nvector<int> solve(vector<int>& nums, int target) {\n    // TODO: implement solution\n    return {};\n}`,
    };
    return templates[lang] || templates.javascript;
  };

  React.useEffect(() => {
    if (!challenge) return;

    const nextCodes: Record<Language, string> = {
      javascript: challenge.starterCode?.javascript || getDefaultStarterCode('javascript'),
      python: challenge.starterCode?.python || getDefaultStarterCode('python'),
      java: challenge.starterCode?.java || getDefaultStarterCode('java'),
      cpp: challenge.starterCode?.cpp || getDefaultStarterCode('cpp'),
    };

    setCodesByLanguage((prev) => ({
      javascript: prev.javascript || nextCodes.javascript,
      python: prev.python || nextCodes.python,
      java: prev.java || nextCodes.java,
      cpp: prev.cpp || nextCodes.cpp,
    }));
  }, [challenge]);

  const code = codesByLanguage[language] || '';
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const handleCodeChange = (value: string | undefined) => {
    setCodesByLanguage((prev) => ({
      ...prev,
      [language]: value || '',
    }));
  };

  const isPlaceholderCode = (source: string) => {
    const stripped = source
      .replace(/\/\/.*$/gm, '')
      .replace(/#.*$/gm, '')
      .replace(/\/\*[\s\S]*?\*\//gm, '')
      .replace(/\s+/g, '')
      .toLowerCase();

    return (
      !stripped ||
      stripped === 'pass' ||
      stripped.includes('todo') ||
      stripped === 'return;' ||
      stripped === 'return' ||
      stripped === 'thrownewerror();' ||
      stripped === 'thrownewerror'
    );
  };

  const handleClearCode = () => {
    setCodesByLanguage((prev) => ({
      ...prev,
      [language]: '',
    }));
    setCopyMessage(null);
    setSubmissionError(null);
  };

  const handleCopyStarterCode = async () => {
    const starterCode = challenge?.starterCode?.[language] || getDefaultStarterCode(language);
    try {
      if (!navigator.clipboard) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(starterCode);
      setCopyMessage('Starter code copied to clipboard.');
    } catch (err) {
      setCopyMessage('Unable to copy starter code.');
    }
  };

  const handleSubmit = () => {
    if (!id) return;
    if (!challenge?.isUnlocked) {
      setSubmissionError('This challenge is locked. Unlock it before submitting.');
      return;
    }
    if (!code.trim() || isPlaceholderCode(code)) {
      setSubmissionError('Please write a valid solution before submitting.');
      return;
    }

    const PLACEHOLDER_PATTERNS = [
      '// TODO: implement solution',
      '// Write your',
      'return [];',
    ];

    const isPlaceholder = PLACEHOLDER_PATTERNS.some(p => code.includes(p)) 
      || code.trim().length < 50;

    if (isPlaceholder) {
      setSubmissionError('Please write your actual solution before submitting!');
      return;
    }

    setSubmissionError(null);
    submitMutation.mutate({
      challengeId: id,
      code,
      language,
    });
  };

  const defineEditorTheme = (monaco: Monaco) => {
    monaco.editor.defineTheme('algonix-warm', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': palette.editor,
        'editorLineNumber.foreground': '#7A6F68',
        'editorLineNumber.activeForeground': '#E8DFD8',
        'editor.lineHighlightBackground': '#2C2927',
        'editorGutter.background': palette.editor,
      },
    });
  };

  const getLanguageId = (lang: string) => {
    const map: Record<string, string> = {
      javascript: 'javascript',
      python: 'python',
      java: 'java',
      cpp: 'cpp',
    };
    return map[lang] || 'javascript';
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    const errorMessage = axios.isAxiosError(error)
      ? error.response?.data?.message || error.message
      : error instanceof Error
      ? error.message
      : 'Unable to load challenge';

    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{errorMessage}</Alert>
      </Container>
    );
  }

  if (!challenge) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">Challenge not found</Alert>
      </Container>
    );
  }

  const panelHeight = isMobile ? 'auto' : 'calc(100vh - 88px)';
  const editorMinHeight = isMobile ? 350 : undefined;

  const problemPanel = (
    <Paper sx={{ height: panelHeight, minHeight: isMobile ? 300 : undefined, overflow: 'auto', border: 0 }}>
      <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ flexGrow: 1 }}>
            {challenge.title}
          </Typography>
          {challenge.isSolved && (
            <Typography variant="body2" sx={{ color: palette.easy, fontWeight: 600, pt: 1 }}>
              Solved
            </Typography>
          )}
        </Box>

        {!challenge.isUnlocked && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This challenge is locked. Solve the prerequisite challenges or take the skill test to unlock it.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', fontSize: 14 }}>
          <Box component="span" sx={{ fontWeight: 600, color: difficultyColor(challenge.difficulty) }}>
            {difficultyLabel[challenge.difficulty] ?? challenge.difficulty}
          </Box>
          {challenge.category && <Box component="span" sx={{ color: 'text.secondary' }}>{challenge.category}</Box>}
          <Box component="span" sx={{ fontFamily: fonts.mono, fontSize: 13 }}>{challenge.points} pts</Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>{challenge.timeLimit}s · {challenge.memoryLimit} MB</Box>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          <Tab label="Description" />
          <Tab label="Examples" />
          <Tab label="Constraints" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            {challenge.description}
          </Typography>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {(Array.isArray(challenge.examples) ? challenge.examples : []).map((example, index) => (
            <Card key={index} sx={{ mb: 2, bgcolor: '#FFFFFF' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                <Typography variant="h6" gutterBottom>
                  Example {index + 1}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Input:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      bgcolor: palette.page,
                      fontFamily: fonts.mono,
                      p: 1.5,
                      borderRadius: 2,
                      fontSize: '0.8rem',
                      overflow: 'auto',
                      maxWidth: '100%',
                    }}
                  >
                    {example.input}
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Output:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      bgcolor: palette.page,
                      fontFamily: fonts.mono,
                      p: 1.5,
                      borderRadius: 2,
                      fontSize: '0.8rem',
                      overflow: 'auto',
                      maxWidth: '100%',
                    }}
                  >
                    {example.output}
                  </Box>
                </Box>
                {example.explanation && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Explanation:
                    </Typography>
                    <Typography variant="body2">{example.explanation}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {challenge.constraints}
          </Typography>
        </TabPanel>
      </Box>
    </Paper>
  );

  const codePanel = (
    <Paper sx={{ height: panelHeight, minHeight: editorMinHeight, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: 0, bgcolor: palette.editor }}>
      <Box sx={{ p: { xs: 1.5, sm: 1.5 }, borderBottom: '1px solid #36322F' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(['javascript', 'python', 'java', 'cpp'] as Language[]).map((lang) => (
              <Button
                key={lang}
                size="small"
                onClick={() => setLanguage(lang)}
                sx={{
                  minHeight: 32,
                  px: 1.5,
                  fontSize: 13,
                  fontWeight: language === lang ? 600 : 500,
                  bgcolor: language === lang ? palette.sand : 'transparent',
                  color: language === lang ? palette.ink : '#B5A89F',
                  '&:hover': { bgcolor: language === lang ? palette.sand : 'rgba(255,255,255,0.06)' },
                }}
              >
                {LANGUAGE_LABELS[lang]}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <Button size="small" onClick={handleClearCode} sx={{ flex: { xs: 1, sm: 'none' }, color: '#D9CEC6', minHeight: 34 }}>
              Clear
            </Button>
            <Button size="small" onClick={handleCopyStarterCode} sx={{ flex: { xs: 1, sm: 'none' }, color: '#D9CEC6', minHeight: 34 }}>
              {isMobile ? 'Copy' : 'Copy starter code'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitMutation.isPending || !code.trim() || !challenge?.isUnlocked}
              sx={{
                flex: { xs: 1, sm: 'none' },
                minHeight: 34,
                bgcolor: palette.sand,
                color: palette.ink,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.08em',
                '&:hover': { bgcolor: '#EDE4DE' },
                '&.Mui-disabled': { bgcolor: '#3A3633', color: '#8A7D75' },
              }}
            >
              {submitMutation.isPending ? <CircularProgress size={18} color="inherit" /> : 'SUBMIT'}
            </Button>
          </Box>
        </Box>
        {(copyMessage || !challenge?.isUnlocked || !code.trim()) && (
          <Box sx={{ mt: 1 }}>
            {!challenge?.isUnlocked ? (
              <Alert severity="warning">This challenge is locked. Unlock it before submitting.</Alert>
            ) : copyMessage ? (
              <Alert severity={copyMessage.includes('Unable') ? 'error' : 'success'}>{copyMessage}</Alert>
            ) : (
              <Alert severity="info">Enter your solution code before submitting.</Alert>
            )}
          </Box>
        )}
        {submissionResult && submissionResult.status && (
          <Box sx={{ mt: 1 }}>
            <Alert severity={submissionResult.status === 'Accepted' ? 'success' : 'info'}>
              Last submission: {submissionResult.status}. Score: {submissionResult.score ?? 'N/A'}%.
            </Alert>
          </Box>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: editorMinHeight || 0 }}>
        <Editor
          key={language}
          height={isMobile ? '350px' : '100%'}
          language={getLanguageId(language)}
          value={code}
          onChange={handleCodeChange}
          beforeMount={defineEditorTheme}
          theme="algonix-warm"
          options={{
            fontFamily: fonts.mono,
            padding: { top: 16 },
            minimap: { enabled: false },
            fontSize: isMobile ? 13 : 14,
            lineNumbers: 'on',
            roundedSelection: false,
            scrollBeyondLastLine: false,
            automaticLayout: true,
          }}
        />
      </Box>

      {submissionError && (
        <Box sx={{ p: 2, bgcolor: palette.card }}>
          <Alert severity="error">{submissionError}</Alert>
        </Box>
      )}

      {submissionResult && (
        <Box sx={{ p: 2, bgcolor: palette.card }}>
          <Alert
            severity={submissionResult.judgeServiceUnavailable ? 'warning' : submissionResult.status === 'Accepted' ? 'success' : 'error'}
            icon={submissionResult.judgeServiceUnavailable ? <ErrorIcon /> : submissionResult.status === 'Accepted' ? <CheckCircle /> : <ErrorIcon />}
          >
            <Typography variant="subtitle2">
              {submissionResult.message} - Score: {submissionResult.score}%
            </Typography>
            {submissionResult.judgeServiceUnavailable && (
              <Typography variant="body2">
                Judge service unavailable. Submission could not be verified. Please try again later.
              </Typography>
            )}
            {submissionResult.pointsEarned > 0 && (
              <Typography variant="body2">
                Points earned: {submissionResult.pointsEarned}
              </Typography>
            )}
          </Alert>

          {Array.isArray(submissionResult.testResults) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Test Results:</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {submissionResult.testResults.map((result: any, index: number) => (
                  <Chip
                    key={index}
                    label={`Test ${result.testCase}`}
                    color={result.passed ? 'success' : 'error'}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 1 }, bgcolor: palette.sand, minHeight: 'calc(100vh - 64px)' }}>
      {isMobile && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={mobileView}
            exclusive
            onChange={(_, val) => val && setMobileView(val)}
            size="small"
            fullWidth
            sx={{ maxWidth: 400 }}
          >
            <ToggleButton value="problem" sx={{ flex: 1, textTransform: 'none' }}>
              <Description sx={{ mr: 0.5, fontSize: 18 }} />
              Problem
            </ToggleButton>
            <ToggleButton value="code" sx={{ flex: 1, textTransform: 'none' }}>
              <CodeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Code
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Grid container spacing={1}>
        {isMobile ? (
          <Grid item xs={12}>
            {mobileView === 'problem' ? problemPanel : codePanel}
          </Grid>
        ) : (
          <>
            <Grid item xs={12} md={6}>
              {problemPanel}
            </Grid>
            <Grid item xs={12} md={6}>
              {codePanel}
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default ChallengeDetail;